import { useEffect } from "react";
import { AxiosError } from "axios";
import debounce from "lodash/debounce";
import memoize from "memoizee";

import type { BasicFarm, Farm, FarmsDataEnvelope } from "../../../models/farms";
import type { FieldsDataEnvelope, Field } from "../../../models/fields";

import { convertDataToDataById } from "../../../helpers/computations";
import {
  QueryStatus,
  consolidateStatuses,
  ensureState,
  ensureStateWithData,
} from "../../../services/query";
import { numberOfFarmsListsPossiblyActiveAtTheSameTime } from "../../../services/helpers/refinements";
import activities from "../../../redux/activities";

import type { ResultOfUseSetFarmsGlobalActivityAndStatus } from "./farms.globalStatus";

/**
 * Compute an enriched farm.
 */
export const computeFarm = (
  basicFarm: BasicFarm,
  fieldsForThisFarm: Field[],
): Farm => {
  return {
    ...basicFarm,
    fields: fieldsForThisFarm,
    fieldsById: convertDataToDataById(fieldsForThisFarm),
  };
};

/**
 * Compute the farms derived data.
 *
 * This is only for use in computeCombinedData.
 * Otherwise, the memoization will not function properly.
 */
export const computeFarms = memoize(
  (
    basicFarms: BasicFarm[],
    fieldsByFarmId: FieldsDataEnvelope["fieldsByFarmId"],
  ) => {
    return basicFarms.map((basicFarm) => {
      const fieldsForThisFarm = fieldsByFarmId[basicFarm.id] ?? [];
      return computeFarm(basicFarm, fieldsForThisFarm);
    });
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[enrichedFarms.query.helpers] computeFarms",
  },
);

/**
 * Compute the farmsById derived data.
 *
 * This is only for use in computeCombinedData.
 * Otherwise, the memoization will not function properly.
 */
export const computeFarmsById = memoize(convertDataToDataById, {
  max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
  profileName: "[enrichedFarms.query.helpers] computeFarmsById",
});

/**
 * Compute the combined farms data envelope.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 * Otherwise, the memoization will not function properly.
 */
export const computeCombinedData = memoize(
  (
    basicFarms: BasicFarm[] | undefined,
    fieldsByFarmId: FieldsDataEnvelope["fieldsByFarmId"] | undefined,
  ): FarmsDataEnvelope | undefined => {
    if (basicFarms === undefined || fieldsByFarmId === undefined) {
      return undefined;
    }

    const farms = computeFarms(basicFarms, fieldsByFarmId);
    const farmsById = computeFarmsById(farms);

    const combinedData: FarmsDataEnvelope = {
      farms,
      farmsById,
    };

    return combinedData;
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[enrichedFarms.query.helpers] computeCombinedData",
  },
);

/**
 * Compute the combined farms status.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 * Otherwise, the memoization will not function properly.
 */
export const computeCombinedStatus = memoize(
  (basicFarmsStatus: QueryStatus, fieldsStatus: QueryStatus) => {
    return consolidateStatuses(basicFarmsStatus, fieldsStatus);
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[enrichedFarms.query.helpers] computeCombinedStatus",
  },
);

/**
 * Compute the combined farms error.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 */
export const computeCombinedError = (
  basicFarmsError: AxiosError | null,
  fieldsError: AxiosError | null,
): AxiosError | null => {
  return basicFarmsError || fieldsError;
};

/**
 * Compute the combined farms envelope, ensuring that it is in a valid state.
 *
 * The memoization of this function is very important
 * as it keeps the same object reference between renders and across hook instances.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 * Otherwise, the memoization will not function properly.
 */
export const computeEnvelopeOfEnsuredState = memoize(
  (
    data: FarmsDataEnvelope | undefined,
    status: QueryStatus,
    error: AxiosError | null,
  ) => {
    return ensureState({ data, status, error });
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName: "[enrichedFarms.query.helpers] computeEnvelopeOfEnsuredState",
  },
);

/**
 * Compute the combined farms envelope, ensuring that it is in a valid state with data.
 *
 * The memoization of this function is very important
 * as it keeps the same object reference between renders and across hook instances.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 * Otherwise, the memoization will not function properly.
 */
export const computeEnvelopeOfEnsuredStateWithData = memoize(
  (data: FarmsDataEnvelope, status: QueryStatus, error: AxiosError | null) => {
    return ensureStateWithData({ data, status, error });
  },
  {
    max: numberOfFarmsListsPossiblyActiveAtTheSameTime,
    profileName:
      "[enrichedFarms.query.helpers] computeEnvelopeOfEnsuredStateWithData",
  },
);

/**
 * The returned value of this will contain
 * the onSuccess handler for useHandleStatusForGlobalStatus
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of hook instances.
 *
 * This is only meant for use in useHandleStatusForGlobalStatus.
 */
export const onSuccess = debounce(
  ({
    setFarmsGlobalActivityAndStatus,
  }: {
    setFarmsGlobalActivityAndStatus: ResultOfUseSetFarmsGlobalActivityAndStatus;
  }) => {
    setFarmsGlobalActivityAndStatus({
      status: "success",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the onError handler for useHandleStatusForGlobalStatus
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of hook instances.
 *
 * This is only meant for use in useHandleStatusForGlobalStatus.
 */
export const onError = debounce(
  ({
    setFarmsGlobalActivityAndStatus,
  }: {
    setFarmsGlobalActivityAndStatus: ResultOfUseSetFarmsGlobalActivityAndStatus;
  }) => {
    setFarmsGlobalActivityAndStatus({
      status: "error",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the onLoading handler for useHandleStatusForGlobalStatus
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of hook instances.
 *
 * This is only meant for use in useHandleStatusForGlobalStatus.
 */
export const onLoading = debounce(
  ({
    setFarmsGlobalActivityAndStatus,
  }: {
    setFarmsGlobalActivityAndStatus: ResultOfUseSetFarmsGlobalActivityAndStatus;
  }) => {
    setFarmsGlobalActivityAndStatus({
      status: "loading",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * This hook contains the logic for use in the hooks of `./enrichedFarms.ts`
 * to handle the status for the global status.
 *
 * This is only for use by the hooks in `./enrichedFarms.ts`.
 */
export const useHandleStatusForGlobalStatus = ({
  status,
  setFarmsGlobalActivityAndStatus,
}: {
  status: QueryStatus;
  setFarmsGlobalActivityAndStatus: ResultOfUseSetFarmsGlobalActivityAndStatus;
}): void => {
  const isStatusLoading = status === "loading";
  const isStatusError = status === "error";
  const isStatusSuccess = status === "success";

  // We using a useEffect here so that this only sets the current operation sub data global
  // activity and status when these values change, as opposed to on every render.
  // This is a pain but is only necessary to do while we preserve the behavior of the deprecated data hooks.

  useEffect(() => {
    if (isStatusLoading) {
      onLoading({ setFarmsGlobalActivityAndStatus });
    }
  }, [isStatusLoading, setFarmsGlobalActivityAndStatus]);

  useEffect(() => {
    if (isStatusError) {
      onError({ setFarmsGlobalActivityAndStatus });
    }
  }, [isStatusError, setFarmsGlobalActivityAndStatus]);

  useEffect(() => {
    if (isStatusSuccess) {
      onSuccess({ setFarmsGlobalActivityAndStatus });
    }
  }, [isStatusSuccess, setFarmsGlobalActivityAndStatus]);
};
